import { Box, Container, Divider, SimpleGrid } from '@hover/blueprint';
import { Button } from '@hover/blueprint/brand';
import { Link as GatsbyLink } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import React, { useEffect, useRef, useState } from 'react';
import ExteriorContractorProductionVector from '../../../assets/svgs/ExteriorContractorProductionVector';
import HoverMaterials from '../../../assets/videos/hover_materials.mp4';
import { DownloadAppModal } from '../../common';
import ArrowButton from '../../ui/ArrowButton';
import CustomAccordion from './CustomAccordion';
import ProductionHeader from './ProductionHeader';

interface Props {
  realTimeListItems: string[];
  orderDirectlyListItems: string[];
}

const ProductionDesktop: React.FC<Props> = ({
  realTimeListItems,
  orderDirectlyListItems,
}) => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isActiveSecond, setIsActiveSecond] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && !isActive) {
      videoRef.current.currentTime = 0;
      void videoRef.current.play();
    }
  }, [isActive]);

  const toggleModal = () => setIsOpenModal(!isOpenModal);

  const onClickFirst = () => {
    if (isActive) {
      setIsActive(!isActive);
      setTimeout(() => {
        setIsActiveSecond(!isActiveSecond);
      }, 300);
    } else {
      setIsActiveSecond(!isActiveSecond);
      setTimeout(() => {
        setIsActive(!isActive);
      }, 300);
    }
  };

  const onClickSecond = () => {
    if (isActiveSecond) {
      setIsActiveSecond(!isActiveSecond);
      setTimeout(() => {
        setIsActive(!isActive);
      }, 300);
    } else {
      setIsActive(!isActive);
      setTimeout(() => {
        setIsActiveSecond(!isActiveSecond);
      }, 300);
    }
  };

  return (
    <SimpleGrid
      spacing={600}
      columns={2}
      d={{ base: 'none', desktop: 'flex' }}
      alignItems="center"
    >
      <DownloadAppModal isOpen={isOpenModal} onClose={toggleModal} />
      <Container flexDir="column" maxW="580px" px={0} mr="auto">
        <ProductionHeader mb="56px" alignSelf="flex-start" />
        <Box flexDir="column" minH="400px">
          <Divider borderWidth="1px" color="neutral.300" my={0} opacity={1} />
          <CustomAccordion
            onClick={onClickFirst}
            isActive={isActive}
            header="Direct ordering"
            listItems={realTimeListItems}
            body="Supplier direct ordering from the HOVER app makes everything simpler."
          />
          <Divider
            my={0}
            borderWidth="1px"
            color="neutral.300"
            mt={isActive ? '26px' : 0}
          />
          <CustomAccordion
            onClick={onClickSecond}
            isActive={isActiveSecond}
            header="Labor management"
            listItems={orderDirectlyListItems}
            body="With just a few clicks, meet every project's specific installation needs"
          />
          <Divider
            my={0}
            borderWidth="1px"
            color="neutral.300"
            mt={!isActive ? '26px' : 0}
          />
        </Box>
        <Box flexDir={{ base: 'column', tablet: 'row' }} mt={800}>
          <Button
            colorScheme="primary"
            variant="solid"
            mb={{ base: 500, desktop: 0 }}
            w={{ base: '100%', tablet: 'auto' }}
            onClick={toggleModal}
          >
            Start measuring in minutes
          </Button>
          <ArrowButton
            target="_blank"
            as={GatsbyLink}
            variant="minimal"
            w={{ base: '100%', tablet: 'auto' }}
            mb={{ base: 600, desktop: 0 }}
            to="/pricing/"
          >
            Go to pricing
          </ArrowButton>
        </Box>
      </Container>
      <Container d="flex" justifyContent="flex-end">
        <Box
          maxW="377px"
          maxH="537px"
          position="relative"
          d={isActive ? 'flex' : 'none'}
          mr={1100}
        >
          <Box position="absolute" top="-100px" right="-100px">
            <ExteriorContractorProductionVector />
          </Box>
          <StaticImage
            loading="lazy"
            placeholder="blurred"
            objectFit="contain"
            alt="real time 3d"
            src="../../../assets/images/exterior_contractor_simple_production_solution.png"
          />
        </Box>
        <Box
          maxH="328px"
          maxW="580px"
          position="relative"
          d={!isActive ? 'block' : 'none'}
        >
          <video ref={videoRef} src={HoverMaterials} muted playsInline loop />
        </Box>
      </Container>
    </SimpleGrid>
  );
};

export default ProductionDesktop;
